import React, { useEffect, useState } from "react";
import { COURSE_BASE_URL } from "../../services/courseService";
import Loading from "../../utils/Loading";
import { useAuthContext } from "../../hooks/useAuthContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import useCartContext from "../../hooks/useCartContext";

const CourseDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuthContext();
  const { addToCart } = useCartContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLesson, setActiveLesson] = useState(0);

  // Initialize with complete structure including all possible fields
  const [course, setCourse] = useState({
    id: "",
    title: "Loading course...",
    instructor: "Unknown Instructor",
    description: "",
    credit: "0",
    price: "0.00",
    category: "IT & Software",
    numberOfCourses: "0",
    courseRating: "5.5",
    createdAt: new Date().toISOString(),
    lessons: [],
  });

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const response = await fetch(
          `${COURSE_BASE_URL}/api/courses/list/${id}`
        );
        if (!response.ok) throw new Error("Failed to fetch course data");
        const data = await response.json();

        // Safely merge the API data with our initial state
        setCourse((prev) => ({
          ...prev,
          ...data,
          // Ensure critical fields are properly set
          id: data.id || id,
          title: data.title || "Untitled Course",
          instructor: data.instructor || "Unknown Instructor",
          price: data.price ? `$${parseFloat(data.price).toFixed(2)}` : "Free",
          lessons: Array.isArray(data.lessons) ? data.lessons : [],
          courseRating: data.courseRating || "0.0",
          numberOfCourses: data.numberOfCourses || "0",
          createdAt: data.createdAt || new Date().toISOString(),
        }));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [id]);

  const handleAddToCart = () => {
    if (!course.id) return;

    addToCart({
      id: course.id,
      title: course.title,
      price: course.price,
    });
    navigate("/api/courses/shopping-cart");
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "80vh" }}
      >
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="alert alert-danger text-center mx-auto my-5"
        style={{ maxWidth: "600px" }}
      >
        <i className="fa fa-exclamation-triangle fa-2x mb-3"></i>
        <h4>Error Loading Course</h4>
        <p>{error.message}</p>
        <Link to="/api/courses" className="btn btn-primary">
          <i className="fa fa-arrow-left"></i> Back to Courses
        </Link>
      </div>
    );
  }

  // Helper variables for conditional rendering
  const hasLessons = course.lessons && course.lessons.length > 0;
  const formattedDate = new Date(course.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="bg-light">
      {/* Course Header Section */}
      <div className="bg-dark text-white py-5">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/api/courses" className="text-white-50">
                  <i className="fa fa-home"></i> Home
                </Link>
              </li>
              <li className="breadcrumb-item text-white-50">
                {course.category}
              </li>
              <li
                className="breadcrumb-item active text-white"
                aria-current="page"
              >
                {course.title}
              </li>
            </ol>
          </nav>

          <div className="row align-items-center">
            <div className="col-lg-8">
              <h1 className="display-5 fw-bold mb-3">{course.title}</h1>
              <p className="lead mb-4">
                {course.description || "No description available."}
              </p>

              <div className="d-flex flex-wrap align-items-center mb-3">
                <div className="me-4 mb-2">
                  <span className="badge bg-warning text-dark">
                    <i className="fa fa-star me-1"></i> {course.courseRating}
                  </span>
                </div>
                <div className="me-4 mb-2">
                  <i className="fa fa-users me-1"></i> 268 &nbsp;
                  {/* {course.numberOfCourses}{" "} */}
                  students
                </div>
                <div className="me-4 mb-2">
                  <i className="fa fa-clock-o me-1"></i> Last updated{" "}
                  {formattedDate}
                </div>
              </div>

              <div className="d-flex align-items-center">
                <div className="me-3">
                  <i className="fa fa-user-circle fa-2x"></i>
                </div>
                <div>
                  <p className="mb-0">Created by</p>
                  <h5 className="mb-0">
                    <Link
                      to={
                        user
                          ? `#/api/authors/author-profile/#`
                          : "/api/users/login/"
                      }
                      className="text-white"
                    >
                      {course.instructor}
                    </Link>
                  </h5>
                </div>
              </div>
            </div>

            <div className="col-lg-4 mt-4 mt-lg-0">
              <div className="card shadow-lg">
                <div className="card-body text-center">
                  <h3 className="card-title text-dark mb-3">{course.price}</h3>

                  {user && user.isAdmin ? (
                    <Link
                      to={`/course/edit/${id}`}
                      className="btn btn-primary btn-lg w-100 mb-3"
                    >
                      <i className="fa fa-edit me-2"></i> Edit Course
                    </Link>
                  ) : (
                    <button
                      onClick={handleAddToCart}
                      className="btn btn-warning btn-lg w-100 mb-3"
                      disabled={!course.id}
                    >
                      <i className="fa fa-cart-plus me-2"></i> ADD TO CART
                    </button>
                  )}

                  <div className="d-grid gap-2">
                    <button className="btn btn-outline-secondary">
                      <i className="fa fa-heart-o me-2"></i> Wishlist
                    </button>
                    <button className="btn btn-outline-secondary">
                      <i className="fa fa-share-alt me-2"></i> Share
                    </button>
                  </div>

                  <hr className="my-3" />

                  <div className="text-start">
                    <h5 className="mb-3">This course includes:</h5>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <i className="fa fa-video-camera text-success me-2"></i>
                        {hasLessons
                          ? `${course.lessons.length} video lessons`
                          : "No video content"}
                      </li>
                      <li className="mb-2">
                        <i className="fa fa-file-text text-info me-2"></i>{" "}
                        {course.credit} downloadable resources
                      </li>
                      <li className="mb-2">
                        <i className="fa fa-mobile text-primary me-2"></i>{" "}
                        Access on mobile and TV
                      </li>
                      <li className="mb-2">
                        <i className="fa fa-certificate text-warning me-2"></i>{" "}
                        Certificate of completion
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content Section */}
      <div className="container py-5">
        <div className="row">
          <div className="col-lg-8">
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <h3 className="mb-0">
                  <i className="fa fa-list-ul text-primary me-2"></i> Course
                  Content
                </h3>
              </div>
              <div className="card-body p-0">
                {hasLessons ? (
                  <div className="list-group list-group-flush">
                    {course.lessons.map((lesson, lessonIndex) => {
                      const hasTopics =
                        lesson.topics && lesson.topics.length > 0;
                      const videoDuration = lesson.content ? (
                        `${Math.ceil(lesson.content.length / 100000)} min`
                      ) : (
                        <>
                          <small>{lesson.topics.length} lessons</small>
                        </>
                      );

                      return (
                        <div
                          key={lessonIndex}
                          className={`list-group-item list-group-item-action ${
                            activeLesson === lessonIndex ? "active" : ""
                          }`}
                          onClick={() => setActiveLesson(lessonIndex)}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h5 className="mb-1">
                                <i className="fa fa-play-circle me-2"></i>
                                Lesson {lessonIndex + 1}:{" "}
                                {lesson.lessonTitle || "Untitled Lesson"}
                              </h5>
                              <small className="text-muted">
                                {hasTopics
                                  ? `${lesson.topics.length} topics`
                                  : "No topics"}{" "}
                                • {videoDuration}
                              </small>
                            </div>
                            <i
                              className={`fa fa-chevron-${
                                activeLesson === lessonIndex ? "down" : "right"
                              }`}
                            ></i>
                          </div>

                          {activeLesson === lessonIndex && (
                            <div className="mt-3">
                              {lesson.content && (
                                <div className="ratio ratio-16x9 mb-3">
                                  <video controls className="rounded">
                                    <source
                                      src={lesson.content}
                                      type="video/mp4"
                                    />
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                              )}

                              {hasTopics ? (
                                <div
                                  className="accordion"
                                  id={`lessonAccordion-${lessonIndex}`}
                                >
                                  {lesson.topics.map((topic, topicIndex) => (
                                    <div
                                      className="accordion-item border-0"
                                      key={topicIndex}
                                    >
                                      <h2
                                        className="accordion-header"
                                        id={`heading-${lessonIndex}-${topicIndex}`}
                                      >
                                        <button
                                          className="accordion-button collapsed"
                                          type="button"
                                          data-bs-toggle="collapse"
                                          data-bs-target={`#collapse-${lessonIndex}-${topicIndex}`}
                                        >
                                          <i className="fa fa-file-text-o me-2"></i>
                                          Topic {topicIndex + 1}:{" "}
                                          {topic.title || "Untitled Topic"}
                                        </button>
                                      </h2>
                                      <div
                                        id={`collapse-${lessonIndex}-${topicIndex}`}
                                        className="accordion-collapse collapse"
                                        aria-labelledby={`heading-${lessonIndex}-${topicIndex}`}
                                      >
                                        <div className="accordion-body">
                                          <p>
                                            {topic.content ||
                                              "No content available for this topic."}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="alert alert-info">
                                  No topics available for this lesson.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 text-center">
                    <i className="fa fa-exclamation-triangle fa-2x text-warning mb-3"></i>
                    <h5>No lessons available</h5>
                    <p className="text-muted">
                      The course content is currently being prepared.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Course Description */}
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <h3 className="mb-0">
                  <i className="fa fa-info-circle text-info me-2"></i>{" "}
                  Description
                </h3>
              </div>
              <div className="card-body">
                <p>
                  {course.description ||
                    "No description available for this course."}
                </p>
              </div>
            </div>
          </div>

          {/* Instructor Section */}
          <div className="col-lg-4">
            <div className="card shadow-sm mb-4">
              <div className="card-header bg-white">
                <h3 className="mb-0">
                  <i className="fa fa-user text-success me-2"></i> Instructor
                </h3>
              </div>
              <div className="card-body text-center">
                <img
                  // src="https://randomuser.me/api/portraits/men/32.jpg"
                  src="https://i.pravatar.cc/500"
                  alt="Instructor"
                  className="rounded-circle mb-3"
                  width="120"
                />
                <h4>{course.instructor}</h4>
                <p className="text-muted">Senior Developer & Instructor</p>
                <div className="d-flex justify-content-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={`fa ${
                        i < Math.floor(parseFloat(course.courseRating))
                          ? "fa-star"
                          : "fa-star-o"
                      } text-warning mx-1`}
                    ></i>
                  ))}
                  <span className="ms-2">{course.courseRating} Rating</span>
                </div>
                <div className="d-flex justify-content-center mb-3">
                  <div className="mx-3">
                    <i className="fa fa-users me-1"></i>{" "}
                    {/* {course.numberOfCourses}  */} 368 Students
                  </div>
                  <div className="mx-3">
                    <i className="fa fa-play-circle me-1"></i> 8 Courses
                  </div>
                </div>
                <p className="text-muted">
                  {course.instructor} is a professional developer with extensive
                  experience teaching programming and software development.
                </p>
                <button className="btn btn-outline-primary w-100">
                  <i className="fa fa-envelope me-2"></i> Contact Instructor
                </button>
              </div>
            </div>

            {/* Reviews Section */}
            <div className="card shadow-sm">
              <div className="card-header bg-white">
                <h3 className="mb-0">
                  <i className="fa fa-star text-warning me-2"></i> Reviews
                </h3>
              </div>
              <div className="card-body">
                <div className="text-center mb-3">
                  <h1 className="display-4">{course.courseRating}</h1>
                  <div className="mb-2">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`fa ${
                          i < Math.floor(parseFloat(course.courseRating))
                            ? "fa-star"
                            : "fa-star-o"
                        } text-warning`}
                      ></i>
                    ))}
                  </div>
                  <small className="text-muted">
                    {" "}
                    &nbsp; Based on &nbsp;
                    {265} &nbsp;
                    {/* {course.numberOfCourses} */}
                    reviews
                  </small>
                </div>

                {[5, 4, 3, 2, 1].map((stars) => {
                  const percentage = Math.floor(Math.random() * 30) + 50; // Random percentage for demo
                  return (
                    <div className="d-flex align-items-center mb-1" key={stars}>
                      <small className="me-2">{stars} stars</small>
                      <div
                        className="progress flex-grow-1"
                        style={{ height: "8px" }}
                      >
                        <div
                          className="progress-bar bg-warning"
                          style={{
                            width: `${
                              stars === 5
                                ? 87
                                : stars === 4
                                ? 55
                                : stars === 3
                                ? 5
                                : stars === 2
                                ? 3
                                : 2
                            }%`,
                          }}
                        ></div>
                      </div>
                      <small className="ms-2">
                        {stars === 5
                          ? "87%"
                          : stars === 4
                          ? "55%"
                          : stars === 3
                          ? "5%"
                          : stars === 2
                          ? "3%"
                          : "2%"}
                      </small>
                    </div>
                  );
                })}

                <button className="btn btn-outline-secondary w-100 mt-3">
                  <i className="fa fa-pencil me-2"></i> Write a Review
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Courses Button */}
      <div className="container pb-5">
        <Link to="/api/courses" className="btn btn-outline-primary btn-lg">
          <i className="fa fa-chevron-left me-2"></i> Back to Courses
        </Link>
      </div>
    </div>
  );
};

export default CourseDetails;
